import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import AdminLayout from './components/AdminLayout';
import { Loading } from './components/common';

// Pages
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import DashboardPage from './pages/DashboardPage';
import ResumesPage from './pages/ResumesPage';
import ResumeEditorPage from './pages/ResumeEditorPage';
import ResumeBuilderPage from './pages/ResumeBuilderPage';
import JobsPage from './pages/JobsPage';
import JobDetailPage from './pages/JobDetailPage';
import PricingPage from './pages/PricingPage';
import SettingsPage from './pages/SettingsPage';
import PrivacyPage from './pages/PrivacyPage';
import SupportPage from './pages/SupportPage';
import HelpPage from './pages/HelpPage';
import ATSCheckerPage from './pages/ATSCheckerPage';
import SharedResumePage from './pages/SharedResumePage';
import FeedbackDashboardPage from './pages/FeedbackDashboardPage';

// Admin Pages
import AdminDashboard from './pages/admin/AdminDashboard';
import CouponsPage from './pages/admin/CouponsPage';
import UsersPage from './pages/admin/UsersPage';
import AffiliatesPage from './pages/admin/AffiliatesPage';

// Landing page - Targeted for laid-off tech workers (beachhead segment)
const LandingPage = () => {
  const { isAuthenticated } = useAuth();

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-primary-900 to-slate-900">
      {/* Hero Section */}
      <div className="flex flex-col items-center justify-center min-h-screen px-4 py-12">
        <div className="text-center text-white max-w-3xl mx-auto">
          {/* Logo */}
          <div className="w-20 h-20 bg-white rounded-2xl flex items-center justify-center mx-auto mb-8 shadow-xl">
            <span className="text-primary-600 font-bold text-4xl">K</span>
          </div>

          {/* Headline - Speaks directly to laid-off tech workers */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
            You spent years building skills.
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-400">
              Don&apos;t let ATS filter you out in 6 seconds.
            </span>
          </h1>

          {/* Subheadline */}
          <p className="text-xl sm:text-2xl opacity-90 mb-4 max-w-2xl mx-auto">
            Kwikly tailors your resume to beat ATS in 60 seconds.
            No more 200 generic applications.
          </p>

          {/* Proof points */}
          <div className="flex flex-wrap justify-center gap-6 mb-8 text-sm sm:text-base opacity-80">
            <div className="flex items-center gap-2">
              <span className="text-green-400">✓</span>
              <span>94% ATS match score</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-green-400">✓</span>
              <span>3X more callbacks</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-green-400">✓</span>
              <span>Built by engineers</span>
            </div>
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-4">
            <a
              href="/register"
              className="px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white font-semibold rounded-lg hover:from-green-600 hover:to-emerald-600 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              Start Beating ATS — Free
            </a>
            <a
              href="/ats-checker"
              className="px-8 py-4 bg-white/10 backdrop-blur text-white font-semibold rounded-lg hover:bg-white/20 transition-colors border border-white/20"
            >
              Check Your ATS Score — No Signup
            </a>
          </div>

          {/* Chrome Extension CTA */}
          <div className="mb-8">
            <a
              href="https://chrome.google.com/webstore/detail/kwikly-ai-job-applications/hpmcmceiaoeladoigpihbhgedohacjhg"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white/5 backdrop-blur text-white font-medium rounded-lg hover:bg-white/10 transition-colors border border-white/10"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 0C8.21 0 4.831 1.757 2.632 4.501l3.953 6.848A5.454 5.454 0 0 1 12 6.545h10.691A12 12 0 0 0 12 0zM1.931 5.47A11.943 11.943 0 0 0 0 12c0 6.012 4.42 10.991 10.189 11.864l3.953-6.847a5.45 5.45 0 0 1-6.865-2.29L1.931 5.47zm13.342 2.166a5.446 5.446 0 0 1 1.45 7.09l.002.001h-.002l-3.952 6.848a12.014 12.014 0 0 0 9.42-5.637 11.952 11.952 0 0 0 1.808-6.304H15.273zM12 8.182a3.818 3.818 0 1 0 0 7.636 3.818 3.818 0 0 0 0-7.636z"/>
              </svg>
              Install Chrome Extension
            </a>
          </div>

          {/* Trust indicators */}
          <p className="text-sm opacity-60 mb-12">
            No credit card required • Free: 3 jobs/week, 2 tailors/month • Pro: $19/month unlimited
            <br />
            <a href="/privacy" className="underline hover:text-white/80">Privacy Policy</a>
          </p>

          {/* Visual demo preview */}
          <div className="relative mx-auto max-w-lg">
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 shadow-2xl">
              <div className="flex items-center justify-between mb-4">
                <span className="text-white/60 text-sm">Your ATS Score</span>
                <span className="text-xs px-2 py-1 bg-green-500/20 text-green-400 rounded-full">
                  +22% improvement
                </span>
              </div>
              <div className="flex items-center gap-6">
                <div className="text-center">
                  <p className="text-white/40 text-xs mb-1">Before</p>
                  <p className="text-3xl font-bold text-red-400">72%</p>
                </div>
                <div className="flex-1 h-2 bg-white/20 rounded-full overflow-hidden">
                  <div className="h-full w-3/4 bg-gradient-to-r from-red-500 via-yellow-500 to-green-500 rounded-full" />
                </div>
                <div className="text-center">
                  <p className="text-white/40 text-xs mb-1">After</p>
                  <p className="text-3xl font-bold text-green-400">94%</p>
                </div>
              </div>
              <p className="text-white/60 text-sm mt-4 text-center">
                See your resume transform in real-time
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Features section */}
      <div className="bg-white py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Why job seekers choose Kwikly
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="w-14 h-14 bg-primary-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🎯</span>
              </div>
              <h3 className="font-semibold text-lg text-gray-900 mb-2">Beat the ATS</h3>
              <p className="text-gray-600">
                Our AI analyzes job descriptions and tailors your resume to match keywords that get past automated filters.
              </p>
            </div>
            <div className="text-center p-6">
              <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">⚡</span>
              </div>
              <h3 className="font-semibold text-lg text-gray-900 mb-2">60 Seconds, Not 2 Hours</h3>
              <p className="text-gray-600">
                Stop spending hours per application. Capture jobs from LinkedIn and tailor instantly.
              </p>
            </div>
            <div className="text-center p-6">
              <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">📈</span>
              </div>
              <h3 className="font-semibold text-lg text-gray-900 mb-2">Track Your Results</h3>
              <p className="text-gray-600">
                See exactly which keywords matched, what&apos;s missing, and track your application success rate.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Final CTA */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 py-12 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
            Stop getting filtered. Start getting interviews.
          </h2>
          <a
            href="/register"
            className="inline-block px-8 py-4 bg-white text-primary-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors shadow-lg"
          >
            Get Started Free — No Credit Card
          </a>
        </div>
      </div>
    </div>
  );
};

// 404 page
const NotFoundPage = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <h1 className="text-6xl font-bold text-gray-300 mb-4">404</h1>
      <p className="text-xl text-gray-600 mb-8">Page not found</p>
      <a
        href="/"
        className="px-6 py-3 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 transition-colors"
      >
        Go Home
      </a>
    </div>
  </div>
);

const App = () => {
  const { loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loading size="lg" text="Loading..." />
      </div>
    );
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
      <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/support" element={<SupportPage />} />
      <Route path="/help" element={<HelpPage />} />
      <Route path="/ats-checker" element={<ATSCheckerPage />} />
      <Route path="/r/:token" element={<SharedResumePage />} />

      {/* Protected routes with layout */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Layout>
              <DashboardPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/jobs"
        element={
          <ProtectedRoute>
            <Layout>
              <JobsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/jobs/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <JobDetailPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/resumes"
        element={
          <ProtectedRoute>
            <Layout>
              <ResumesPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/resumes/new"
        element={
          <ProtectedRoute>
            <Layout>
              <ResumeEditorPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/resumes/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <ResumeEditorPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/build"
        element={
          <ProtectedRoute>
            <ResumeBuilderPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Layout>
              <SettingsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/feedback"
        element={
          <ProtectedRoute>
            <Layout>
              <FeedbackDashboardPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Admin routes */}
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <AdminLayout>
              <AdminDashboard />
            </AdminLayout>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/coupons"
        element={
          <AdminRoute>
            <AdminLayout>
              <CouponsPage />
            </AdminLayout>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <AdminRoute>
            <AdminLayout>
              <UsersPage />
            </AdminLayout>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/affiliates"
        element={
          <AdminRoute>
            <AdminLayout>
              <AffiliatesPage />
            </AdminLayout>
          </AdminRoute>
        }
      />

      {/* Catch-all */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

export default App;
